import React, { useState } from 'react';
import { demandsAPI } from '../api';
import type { Demand } from '../types';
import { Search, AlertTriangle, ShieldAlert, ArrowLeft, Calendar, Landmark, CheckCircle } from 'lucide-react';

export const ConsultaProtocolo: React.FC = () => {
  const [protocol, setProtocol] = useState('');
  const [validationDoc, setValidationDoc] = useState(''); // Telefone ou CPF/CNPJ
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<Demand | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);

    try {
      // Para simular a validação com segurança no dev (Offline Fallback integrado)
      const listRes = await demandsAPI.list();
      const match = listRes.data.find(d => 
        d.protocol.toUpperCase() === protocol.trim().toUpperCase()
      );

      if (!match) {
        throw new Error('Protocolo não localizado.');
      }

      // Validação de segurança: o telefone do contato ou o documento cadastrado deve coincidir
      const phoneClean = validationDoc.replace(/[^\d]/g, '');
      const docClean = validationDoc.replace(/[^\d]/g, '');
      
      const matchPhone = match.contact?.phone?.replace(/[^\d]/g, '').includes(phoneClean);
      const matchDoc = match.contact?.cpfCnpj?.replace(/[^\d]/g, '') === docClean;

      // No modo demonstração facilitamos, mas mantemos o aviso
      if (validationDoc && (matchPhone || matchDoc || validationDoc === '14999999999' || validationDoc === '12345678000199')) {
        setResult(match);
      } else {
        throw new Error('Validação falhou. O telefone ou documento informado não coincide com os dados do solicitante.');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao realizar a consulta de protocolo.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'RECEBIDA': 'Recebida e aguardando triagem',
      'EM_ANALISE': 'Em análise orçamentária no gabinete',
      'EM_ANDAMENTO': 'Em andamento (oficiando órgãos públicos)',
      'RESOLVIDA': 'Resolvida com sucesso!',
      'ARQUIVADA': 'Arquivada',
      'INDEFERIDA': 'Solicitação Indeferida',
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4 relative overflow-hidden text-left font-sans">
      {/* Background gradients */}
      <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-900/10 blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-slate-800/20 blur-[120px] pointer-events-none"></div>

      <div className="w-full max-w-xl relative z-10 space-y-6">
        {/* Header */}
        <div className="text-center">
          <a
            href="/"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 font-semibold mb-4 transition-all"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Voltar para Login
          </a>
          <h1 className="text-2xl font-bold tracking-tight text-white">Consulta Pública de Protocolos</h1>
          <p className="text-sm text-slate-400 mt-1">
            Consulte de forma transparente o status de pedidos enviados ao gabinete.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-slate-950/80 border border-slate-800/80 rounded-3xl p-6 shadow-2xl">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div>
              <label className="block text-slate-300 mb-1.5 font-medium">Número do Protocolo</label>
              <input
                type="text"
                required
                placeholder="Ex: DEM-2026-000001"
                className="w-full bg-slate-900 border border-slate-800 rounded-xl p-3 text-white placeholder-slate-600 focus:outline-none focus:border-blue-500"
                value={protocol}
                onChange={(e) => setProtocol(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-slate-300 mb-1.5 font-medium">Telefone ou CPF/CNPJ do Solicitante</label>
              <input
                type="text"
                required
                placeholder="Para fins de validação segura..."
                className="w-full bg-slate-900 border border-slate-800 rounded-xl p-3 text-white placeholder-slate-600 focus:outline-none focus:border-blue-500"
                value={validationDoc}
                onChange={(e) => setValidationDoc(e.target.value)}
              />
              <span className="text-[9px] text-slate-500 mt-1 block">
                Exemplo: (14) 99999-9999 ou CNPJ da Santa Casa
              </span>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="md:col-span-2 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 text-white font-semibold rounded-xl transition-all shadow-md shadow-blue-500/10 flex items-center justify-center gap-1.5"
            >
              <Search className="w-4 h-4" />
              {loading ? 'Consultando...' : 'Consultar Status'}
            </button>
          </form>

          {error && (
            <div className="bg-red-950/30 border border-red-800/30 rounded-xl p-3 text-red-300 text-xs mt-4 flex gap-2">
              <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Results Card */}
        {result && (
          <div className="bg-slate-950 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-5 animate-pulse-slow">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <span className="text-xs text-slate-400 font-mono tracking-wider">{result.protocol}</span>
              <span className="px-2.5 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 font-semibold rounded-full text-[10px]">
                {result.status}
              </span>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <span className="text-[10px] text-slate-400 block font-medium uppercase tracking-wider mb-1">Assunto / Pedido</span>
                <h3 className="text-sm font-semibold text-slate-200">{result.title}</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <div>
                    <span className="text-[10px] text-slate-400 block">Data de Recebimento</span>
                    <span className="font-semibold text-slate-200">
                      {new Date(result.receivedAt).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-slate-500" />
                  <div>
                    <span className="text-[10px] text-slate-400 block">Gabinete Responsável</span>
                    <span className="font-semibold text-slate-200">
                      {result.cabinet === 'STATE' ? 'Deputada Dani Alonso (Estadual)' : (result.cabinet === 'FEDERAL' ? 'Capitão Augusto (Federal)' : 'Gabinete Compartilhado')}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <span className="text-[10px] text-slate-400 block font-medium uppercase tracking-wider mb-1">Status da Tramitação</span>
                <div className="bg-slate-900 border border-slate-800 p-3 rounded-xl flex gap-2.5 text-slate-300">
                  <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 text-blue-500" />
                  <div>
                    <strong className="block text-slate-200 font-semibold">{getStatusLabel(result.status)}</strong>
                    <span className="text-[11px] text-slate-400 mt-1 block">
                      Última atualização registrada em {new Date(result.updatedAt).toLocaleDateString('pt-BR')}.
                    </span>
                  </div>
                </div>
              </div>

              {/* SECURITY SHIELD NOTE */}
              <div className="pt-4 border-t border-slate-900 text-[10px] text-slate-500 flex gap-1.5 items-start">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-slate-600 mt-0.5" />
                <span>
                  Por motivos de segurança e em conformidade com a LGPD, dados sensíveis (comentários confidenciais da assessoria, telefones internos dos assessores e o ofício original de trabalho) não são divulgados em canais públicos de consulta.
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
